import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Button, Input, List, Space, Typography, Popconfirm, Tooltip, Select, Row, Col, Grid, message } from 'antd';
import { PlusOutlined, DeleteOutlined, DownloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { deleteEventIndexes, exportEventIndexes, getEventIndexes, postEventIndexes } from '../eventIndex/hooks';
import { useTranslation } from 'react-i18next';
import { closeAllSocketsExcept, initSocketLive, sendSocketEvent } from '../page/utils/socket';
import store from '../redux/store';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { useFormEvent } from '../page/utils/hooks';
const { Text } = Typography;
const { useBreakpoint } = Grid;

const INDEX_ROLE_KEYS = [
  'president',
  'presidentF',
  'vicePresident',
  'vicePresidentF',
  'legalAdvisor',
  'secretary',
  'directorGeneral',
  'directorGeneralF',
  'dgRepresentative',
  'dgRepresentativeF',
  'cePresident',
  'cePresidentF',
  'observer',
  'ngoObserver',
];

function formatTime(seconds) {
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${hrs}:${mins}:${secs}`;
}

function formatReadableDate(isoString) {
  const date = new Date(isoString);
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
}

function IndexEvent() {
  const location = useLocation();
  const screens = useBreakpoint();
  const history = useHistory();
  const modalContent = location.state?.modalContent;
  const event = location.state?.event;
  const timeCodeInputWidth = screens.xs ? 200 : 300;
  const selectInputCountry = screens.xs ? 100 : screens.sm ? 200 : 300;

  const [indexes, setIndexes] = useState([]);
  const [currentTimecode, setCurrentTimecode] = useState('');
  const [text, setText] = useState(undefined);
  const [isEditing, setIsEditing] = useState('');

  // Use ref to track if we're currently performing an operation
  const isProcessingRef = useRef(false);
  const lastUpdateRef = useRef(null);

  const { t, i18n } = useTranslation();
  const eventDelayTracked = useSelector((state) => state.reducer.eventDelay);
  const actionIndexation = useSelector((state) => state.reducer.actionIndexation);
  const [countries, setCountries] = useState([])
  const {getCountries} = useFormEvent()

  useEffect(() => {
  getCountries(i18n.language)
    .then((res) => {
      const unique = [];
      const seen = new Set();
 
      (res?.data?.data || []).forEach(({ country_code, country_name }) => {
        const key = country_name.trim().toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          unique.push({ country_code, country_name: country_name.trim() });
        }
      });
 
      setCountries(unique);
    })
    .catch(() => setCountries([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [i18n.language]);
const countryOptions = useMemo(() => {
  const lang = i18n.language || 'en'
  const list = countries.map(({country_code,country_name}) => {
    return {
      label: `${country_name} (${country_code})`,
      value: country_name,
      code: country_code,
    }
  })
  return list.sort((a, b) => a.label.localeCompare(b.label, lang))
}, [countries,i18n.language])

  // Preset roles shown before the countries (translated, deduplicated by label)
  const indexOptions = useMemo(() => {
    const seen = new Set();
    const roles = [];
    INDEX_ROLE_KEYS.forEach((key) => {
      const label = t(`IndexRoles.${key}`);
      const normalized = label.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        roles.push({ label, value: label });
      }
    });
    return [
      ...roles,
      ...countryOptions.filter((opt) => !seen.has(opt.value?.toLowerCase())),
    ];
  }, [countryOptions, t]);

  // Enhanced synchronization effect with debouncing
  useEffect(() => {
    if (!actionIndexation || !event?.id) return;

    // Ignore updates from our own actions (debounce 500ms)
    const now = Date.now();
    if (lastUpdateRef.current && now - lastUpdateRef.current < 500) {
      return;
    }

    // Check if this update is for our event
    if (actionIndexation.updatedIndex?.eventId !== event.id &&
      actionIndexation.eventId !== event.id) {
      return;
    }

    if (actionIndexation.updateType === 'partial' && actionIndexation.updatedIndex) {
      const { updatedIndex } = actionIndexation;

      setIndexes(prevIndexes => {
        const newIndexes = [...prevIndexes];
        const indexToUpdate = newIndexes.findIndex(idx => idx.id === updatedIndex.id);

        if (indexToUpdate !== -1) {
          // Update existing index
          newIndexes[indexToUpdate] = {
            ...newIndexes[indexToUpdate],
            ...updatedIndex
          };
        } else {
          // Add new index
          newIndexes.push(updatedIndex);
        }

        // Sort by start time and reassign numbers
        return newIndexes
          .sort((a, b) => a.start - b.start)
          .map((item, i) => ({ ...item, number: i }));
      });
    }
    else if (actionIndexation.updateType === 'delete' && actionIndexation.chapterId) {
      setIndexes(prevIndexes => {
        return prevIndexes
          .filter(idx => idx.id !== actionIndexation.chapterId)
          .map((item, i) => ({ ...item, number: i }));
      });
    }
    else if (!actionIndexation.updateType && Array.isArray(actionIndexation)) {
      // Full list replacement
      setIndexes(actionIndexation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionIndexation, event?.id]);

  const mergedOptions = useMemo(() => {
    const exists = indexOptions.some(
      opt => opt.value?.toLowerCase() === text?.toLowerCase()
    );
    return !text || exists
      ? indexOptions
      : [{ label: `${text}`, value: text }, ...indexOptions];
  }, [text, indexOptions]);

  const mergedEditOptions = useMemo(() => {
  if (!isEditing) return indexOptions

  const normalized = isEditing.toLowerCase()
  const exists = indexOptions.some(
    opt => opt.value?.toLowerCase() === normalized
  )
  if (exists) return indexOptions
  return [{ label: isEditing, value: isEditing, code: '' }, ...indexOptions]
}, [isEditing, indexOptions])
  const filterOptions = (input, option) => {
    const codeMatch = option.code?.toLowerCase()?.startsWith(input?.toLowerCase() || '');
    const nameMatch = option.label?.toLowerCase()?.includes(input?.toLowerCase() || '');
    return codeMatch || nameMatch;
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isTyping =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable);
      if (!isTyping && (e.code === 'Space' || e.key === ' ')) {
        e.preventDefault();
        captureTimecode();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, eventDelayTracked]);

  const captureTimecode = () => {
    const now = new Date();
    const delayInMs = eventDelayTracked ? eventDelayTracked * 1000 : 0;
    const adjustedNow = new Date(now.getTime() - delayInMs);
    const start = new Date(event?.StartRecordTime);
    if (adjustedNow > start) {
      const diffInSeconds = Math.round((adjustedNow - start) / 1000);
      const timecode = formatTime(diffInSeconds);
      if (timecode) setCurrentTimecode(timecode);
    }
  };
  const handleDownload = (xmlContent) => {
    const blob = new Blob([xmlContent], {
      type: 'application/xml;charset=utf-16',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const eventTitle = event?.video?.title;
    link.download = eventTitle ? eventTitle + ".xml" : 'chapters.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const exportChapters = async () => {
    const eventId = event?.video?.id;
    if (eventId) {
      const res = await exportEventIndexes(eventId);
      if (res.data) {
        handleDownload(res.data);
      }
    }
  };

  const addIndex = async () => {
    if (isProcessingRef.current) return;
    if (!currentTimecode || !/^\d{2}:\d{2}:\d{2}$/.test(currentTimecode)) {
      message.warning(t("Modal.invalidTimecode") || "Invalid timecode. Please capture a valid timecode first.");
      return;
    }

    isProcessingRef.current = true;
    lastUpdateRef.current = Date.now();

    try {
      const [hrs, mins, secs] = currentTimecode.split(':');
      const start = parseInt(hrs) * 3600 + parseInt(mins) * 60 + parseInt(secs);
      const selectedCountry = countryOptions.find(
        (c) => c.value.toLowerCase() === (text || '').toLowerCase()
      );

      const newIndex = {
        id: null,
        number: indexes.length,
        start,
        timeCode: currentTimecode,
        title: text || '',
        type: 'chapter',
        country_code: selectedCountry?.code || null,
      };

      const updatedIndexes = [...indexes, newIndex];
      const videoId = event?.video?.id;

      if (videoId) {
        const res = await postEventIndexes(videoId, transformToAPIFormat(updatedIndexes));

        if (res?.data?.length) {
          const lastItem = res.data[res.data.length - 1];
          const createdIndex = {
            ...newIndex,
            eventId: event?.id,
            id: lastItem.id,
            number: lastItem.number,
            start: lastItem.start,
            title: lastItem.title,
            country_code: lastItem.country_code || null,
          };

          // Transform all data
          const transformed = res.data.map(item => ({
            id: item.id,
            number: item.number,
            start: item.start,
            timeCode: formatTime(item.start),
            title: item.title || '',
            type: item.type,
            country_code: item.country_code || null,
            eventId: event?.id
          }));

          transformed.sort((a, b) => a.start - b.start);

          // Update local state first for instant feedback
          setIndexes(transformed);

          // Then broadcast to other clients
          sendSocketEvent(event?.id, 'event_crud', {
            data: event?.id,
            eventId: event?.id,
            updateType: 'partial',
            updatedIndex: createdIndex
          });

          setCurrentTimecode('');
          setText('');
        }
      }
    } catch (err) {
      console.error('Error while posting new index', err);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const deleteIndex = async (ch) => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    lastUpdateRef.current = Date.now();

    try {
      const videoId = event?.video?.id;

      if (videoId && ch.id) {
        // Optimistic update
        const updated = indexes
          .filter((item) => item.id !== ch.id)
          .map((item, i) => ({ ...item, number: i }));

        setIndexes(updated);

        // Delete from API
        await deleteEventIndexes(videoId, [ch.id]);

        // Broadcast to other clients
        sendSocketEvent(event?.id, 'event_crud', {
          data: event?.id,
          eventId: event?.id,
          updateType: 'delete',
          deletedId: ch.number,
          chapterId: ch.id
        });
      }
    } catch (err) {
      console.error('Error deleting index:', err);
      // Revert on error - refetch from server
      const videoId = event?.video?.id;
      if (videoId) {
        const res = await getEventIndexes(videoId);
        const data = res?.data?.data || [];
        const transformed = data.map(item => ({
          id: item.id,
          number: item.number,
          start: item.start,
          timeCode: formatTime(item.start),
          title: item.title || '',
          type: item.type,
          country_code: item.country_code || null,
        }));
        transformed.sort((a, b) => a.start - b.start);
        setIndexes(transformed);
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  const updateIndex = async (value, index, key) => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    lastUpdateRef.current = Date.now();

    try {
      const updated = [...indexes];
      updated[index][key] = value;

      if (key === 'timeCode') {
        const [hrs, mins, secs] = value.split(':');
        updated[index].start = parseInt(hrs) * 3600 + parseInt(mins) * 60 + parseInt(secs);
      }
      if (key === 'title') {
        const selected = countryOptions.find(
          (c) => c.value.toLowerCase() === value.toLowerCase()
        );
        updated[index].country_code = selected?.code || null;
      }

      const videoId = event?.video?.id;

      if (videoId) {
        const res = await postEventIndexes(videoId, transformToAPIFormat(updated));

        if (res?.data?.length) {
          const updatedFromAPI = res.data[index];
          updated[index] = {
            ...updated[index],
            id: updatedFromAPI.id,
            eventId: event?.id,
            start: updatedFromAPI.start,
            number: updatedFromAPI.number,
          };

          updated.sort((a, b) => a.start - b.start);

          // Update local state
          setIndexes(updated);
          setIsEditing('');

          // Broadcast to other clients
          const updatedItem = {
            ...updated[index],
            eventId: event?.id
          };

          sendSocketEvent(event?.id, 'event_crud', {
            data: event?.id,
            eventId: event?.id,
            updateType: 'partial',
            updatedIndex: updatedItem
          });
        }
      }
    } catch (err) {
      console.error('Error updating index:', err);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const connectWebsocketEvent = useCallback((event) => {
    if (!event?.id) return;
    closeAllSocketsExcept(event.id);
    initSocketLive(event.id, null, "event_indexing", store);
  }, []);

  useEffect(() => {
    const videoId = event?.video?.id;
    if (videoId) {
      connectWebsocketEvent(event);
      getEventIndexes(videoId).then((res) => {
        const data = res?.data?.data || [];
        const transformed = data.map(item => ({
          id: item.id,
          number: item.number,
          start: item.start,
          timeCode: formatTime(item.start),
          title: item.title || '',
          type: item.type,
          country_code: item.country_code || null,
        }));
        transformed.sort((a, b) => a.start - b.start);
        setIndexes(transformed);
      }).catch((err) => console.error('Error fetching indexes', err));
    }

    // Cleanup on unmount
    return () => {
      isProcessingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, connectWebsocketEvent,actionIndexation]);

  const transformToAPIFormat = (list) => list.map((item, index) => ({
    order: index,
    title: item.title || '',
    timeCode: item.timeCode,
    type: item.type,
    countryCode: item.country_code || null,
  }));

  return (
    <>
      <Row className="emp_index">
        <Col className="emp-col col_index emp-col_title">
          <ArrowLeftOutlined
            style={{ fontSize: "20px", cursor: "pointer", marginInline: '0.5rem' }}
            onClick={() => history.goBack()}
          />
          <span className="title">
            {t("Translate.Index_event")} : {event?.title}
          </span>
        </Col>
      </Row>

      <div className='indexation_page'>
        <div className="bloc-form iframe_index iframe-wrapper">
          <iframe
            title="player_index"
            src={modalContent}
            className="flow-iframe"
            width="100%"
            height="100%"
            style={{ aspectRatio: "16/9" }}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            webkitallowfullscreen=""
            mozallowfullscreen
            frameBorder="0"
          />
        </div>

        <div className='bloc-form index_form'>
          {event?.StartRecordTime && (
            <h3 style={{ marginBottom: '16px', marginLeft: '1em' }}>
              {t("Modal.dateIndex")}: {formatReadableDate(event?.StartRecordTime)}
            </h3>
          )}

          <div style={{marginInline: '3em', marginBottom: '1em', maxHeight: '100%', display: 'flex', height:'79%',flexDirection: 'column', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden', width: '90%' }} className='div_form'>

            <Row justify="space-between" align="middle" style={{ padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
              <Col>
                <h3 style={{ margin: 0 }}>{t("Modal.listIndex")}</h3>
              </Col>
              <Col>
                <DownloadOutlined
                  onClick={exportChapters}
                  style={{ fontSize: '18px', cursor: 'pointer' }}
                />
              </Col>
            </Row>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }} className='inner_form_indexation'>
              {indexes.length ? (
                <List
                  dataSource={indexes}
                  renderItem={(item, index) => (
                    <List.Item
                      style={{ paddingLeft: 0 }}
                      actions={[
                        <Popconfirm
                          title={t("Modal.deleteIndex")}
                          onConfirm={() => deleteIndex(item)}
                          key="delete"
                        >
                          <Button danger shape="circle" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ]}
                    >
                      <Space>
                        <Input
                          value={item.timeCode}
                          style={{ width: 100 }}
                          maxLength={8}
                          onChange={(e) => {
                            const value = e.target.value;
                            const cleaned = value.replace(/[^0-9:]/g, '');
                            const isValidFormat = /^(\d{0,2})(:)?(\d{0,2})?(:)?(\d{0,2})?$/.test(cleaned);

                            if (isValidFormat) {
                              const updated = [...indexes];
                              updated[index].timeCode = cleaned;
                              setIndexes(updated);
                            }
                          }}
                          onPressEnter={(e) => {
                            if (/^\d{2}:\d{2}:\d{2}$/.test(e.target.value)) {
                              updateIndex(e.target.value, index, 'timeCode');
                            }
                          }}
                          onBlur={(e) => {
                            if (/^\d{2}:\d{2}:\d{2}$/.test(e.target.value)) {
                              updateIndex(e.target.value, index, 'timeCode');
                            }
                          }}
                        />
                        <Select
                          showSearch
                          defaultActiveFirstOption={false}
                          showArrow={false}
                          value={item.title}
                          style={{ width: selectInputCountry }}
                          placeholder={t('Modal.optionalTitleIndex')}
                          options={mergedEditOptions}
                          filterOption={filterOptions}
                          onSearch={(val) => {
                            const updated = [...indexes];
                            setIsEditing(val);
                            updated[index].title = val;
                            setIndexes(updated);
                          }}
                          onChange={(val) => {
                            updateIndex(val, index, 'title');
                          }}
                          onBlur={() => {
                            const val = indexes[index].title;
                            if (val) {
                              updateIndex(val, index, 'title');
                            }
                          }}
                          onSelect={(val) => {
                            updateIndex(val, index, 'title');
                          }}
                        />
                      </Space>
                    </List.Item>
                  )}
                />
              ) : null}
            </div>

            <div style={{ borderTop: '1px solid #f0f0f0', padding: 16 }}>
<>
                <Space>
                  <Tooltip title={t("Modal.indexIndex")}>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<PlusOutlined />}
                      onClick={captureTimecode}
                    />
                  </Tooltip>
                  <Text>({t("Modal.displayTimecodeIndex")})</Text>
                </Space>

                {currentTimecode && (
                  <>
                    <div style={{ marginTop: 16 }}>
                      <Text strong>Timecode:</Text> {currentTimecode}
                    </div>
                    <Space style={{ marginTop: 8, display: 'flex' }}>
                      <Select
                        showSearch
                        value={text || undefined}
                        placeholder={t('Modal.optionalTitleIndex')}
                        defaultActiveFirstOption={false}
                        showArrow={false}
                        filterOption={filterOptions}
                        onChange={(value) => setText(value)}
                        onSearch={(value) => setText(value)}
                        notFoundContent={null}
                        options={mergedOptions}
                        style={{ width: timeCodeInputWidth }}
                      />
                      <Button type="primary" onClick={addIndex}>
                        {t("Modal.addIndex")}
                      </Button>
                    </Space>
                  </>
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default IndexEvent;